import { useMemo } from 'react';
import { AppLayout } from '@scale/scaleui';
import { useMount } from 'react-use-v13';
import { CorpProjectGroupManagerHomeHeader } from '../../components/CorpProjectGroupManagerHomeHeader';
import { HomeViewCardProps } from './HomeViewCard';
import { BrowserPaths } from '@scale/shared/corpSearch';
import { GenAIOpsHubPaths } from '../../constants';
import { useUser } from 'react-scale/components/ui/UserContext';
import { Section } from './HomeSection';
import { IconName } from '@fortawesome/free-solid-svg-icons';
import { useUnleashFeatureFlagWithLoading } from 'react-scale/hooks/useUnleashFeatureFlag';
import { Button } from '@scale/scaleui-radix';
import { useProjectRoleCheckupModalStore } from 'react-scale/corp/views/CorpProjectGroupManager/components/ProjectRoleStaffingReminder';

type SectionKey = 'delivery' | 'growth' | 'admin';

// By default these are corp-only cards
export const CARD_ITEMS: (HomeViewCardProps & {
  key: string;
  nonCorpUsersAllowed?: boolean;
  section: SectionKey;
  shortTitle: string;
  icon: IconName;
})[] = [
  {
    key: 'gen-ai-projects',
    title: 'Projects',
    shortTitle: 'Projects',
    description: 'Manage projects & deliver data',
    icon: 'rectangle-history',
    to: GenAIOpsHubPaths.Projects,
    section: 'delivery',
  },
  {
    key: 'compass',
    title: 'Data Compass',
    shortTitle: 'Data Compass',
    description: 'Data Workflow Platform',
    icon: 'compass',
    to: GenAIOpsHubPaths.Compass,
    section: 'delivery',
  },
  {
    key: 'allocation',
    title: 'Allocation',
    shortTitle: 'Allocation',
    description: 'Allocate contributors across projects',
    icon: 'shuffle',
    to: GenAIOpsHubPaths.Allocation,
    section: 'delivery',
  },
  {
    key: 'model-explorer',
    title: 'Model Explorer',
    shortTitle: 'Models',
    description: 'Shop all available model endpoints',
    icon: 'cart-shopping',
    to: GenAIOpsHubPaths.ModelExplorer,
    section: 'delivery',
  },
  {
    key: 'toolbox',
    title: 'Toolbox',
    shortTitle: 'Toolbox',
    description: 'All operator tools',
    icon: 'toolbox',
    to: GenAIOpsHubPaths.Toolbox,
    section: 'delivery',
  },
  {
    key: 'course-library',
    title: 'Course Library',
    shortTitle: 'Courses',
    description: 'Collection of courses',
    icon: 'book',
    to: GenAIOpsHubPaths.CourseLibrary,
    section: 'delivery',
  },
  {
    key: 'question-bank',
    title: 'Question Banks',
    shortTitle: 'Question Banks',
    description: 'Manage Question Banks',
    icon: 'shelves',
    to: GenAIOpsHubPaths.QuestionBanks,
    section: 'delivery',
  },
  {
    key: 'experiment-platform',
    title: 'Experiment Platform',
    shortTitle: 'Experiment Platform',
    description: 'Run A/B tests with experiments',
    icon: 'flask',
    to: BrowserPaths.ExperimentPlatform,
    section: 'delivery',
  },
  {
    key: 'gen-ai-growth',
    title: 'Acquisition',
    shortTitle: 'Acquisition',
    description: 'Manage your outbound and recruiting funnels',
    icon: 'seedling',
    to: BrowserPaths.GrowthCenterResumeScreening,
    section: 'growth',
  },
  {
    key: 'communications-center',
    title: 'Communications',
    shortTitle: 'Communications',
    description: 'Manage contributor communications',
    icon: 'paper-plane-top',
    to: BrowserPaths.GrowthCenterCommunication,
    section: 'growth',
  },
  {
    key: 'community-management',
    title: 'Community Management',
    shortTitle: 'Community',
    description: 'Manage your contributor Discourse channels',
    icon: 'bullhorn',
    to: GenAIOpsHubPaths.CommunityManagement,
    section: 'growth',
  },
  {
    key: 'pod-lead-center',
    title: 'Pod Lead Center',
    shortTitle: 'Pod Lead',
    description: 'Monitor cost and approve budget requests',
    icon: 'wallet',
    to: GenAIOpsHubPaths.PodLeadCenter,
    section: 'admin',
  },
  {
    key: 'screening',
    title: 'Screening',
    shortTitle: 'Screening',
    description: 'View automated skill screenings',
    icon: 'pencil',
    to: GenAIOpsHubPaths.ScreeningCenter,
    section: 'delivery',
  },
  {
    key: 'team-lead-dashboard',
    title: 'Team Lead Dashboard',
    shortTitle: 'Team Lead',
    description: 'Manage expert performance',
    icon: 'chart-line-up',
    to: BrowserPaths.ExpertManagement,
    section: 'admin',
  },
  {
    key: 'qc-center',
    title: 'QC Center',
    shortTitle: 'QC Center',
    description: 'Manage quality control',
    icon: 'clipboard-check',
    to: GenAIOpsHubPaths.QCCenter,
    section: 'admin',
  },
  {
    key: 'trust-and-safety-center',
    title: 'T&S Center',
    shortTitle: 'T&S',
    description: 'View policies and manage enforcement',
    icon: 'shield',
    to: GenAIOpsHubPaths.TrustAndSafetyCenter,
    section: 'admin',
  },
  {
    key: 'gaius-projects-center',
    title: 'Gaius Projects Center',
    shortTitle: 'Projects',
    description: 'Track and manage bug fixes across projects',
    icon: 'bug',
    to: GenAIOpsHubPaths.GaiusProjectsCenter,
    section: 'delivery',
  },
  {
    key: 'webinar-hub',
    title: 'Webinar Hub',
    shortTitle: 'Webinars',
    description: 'Manage webinars',
    icon: 'tv',
    to: GenAIOpsHubPaths.Webinars,
    section: 'delivery',
  },
  {
    key: 'ots',
    title: 'OTS Center',
    shortTitle: 'OTS',
    description: 'Manage OTS data',
    icon: 'screwdriver-wrench',
    to: GenAIOpsHubPaths.Ots,
    section: 'delivery',
  },
];

// Define sections using the card items
const SECTIONS = [
  {
    title: 'Delivery',
    items: CARD_ITEMS.filter(card => card.section === 'delivery').map(card => ({
      key: card.key,
      title: card.title,
      description: card.description,
      to: card.to,
      icon: card.icon,
    })),
  },
  {
    title: 'Growth',
    items: CARD_ITEMS.filter(card => card.section === 'growth').map(card => ({
      key: card.key,
      title: card.title,
      description: card.description,
      to: card.to,
      icon: card.icon,
    })),
  },
  {
    title: 'Admin',
    items: CARD_ITEMS.filter(card => card.section === 'admin').map(card => ({
      key: card.key,
      title: card.title,
      description: card.description,
      to: card.to,
      icon: card.icon,
    })),
  },
];

export default function HomeView() {
  useMount(() => (document.title = 'GenAI Ops Hub'));
  const { setShowModal } = useProjectRoleCheckupModalStore();
  const user = useUser();

  const { data: canSeeTnsCenter } = useUnleashFeatureFlagWithLoading(
    'tns-center',
    { userId: user.id },
    false,
  );

  const { data: gaiusProjectsCenterEnabled } = useUnleashFeatureFlagWithLoading(
    'gaius_projects_center',
    { userId: user.id },
    false,
  );

  const { data: webinarHubFlag } = useUnleashFeatureFlagWithLoading(
    'webinar-hub',
    { userId: user.id },
    false,
  );

  const filteredSections = useMemo(() => {
    const { isCorporateUser } = user;
    if (!isCorporateUser) return [];
    return SECTIONS;
  }, [user]);

  return (
    <AppLayout
      className="scaleui scale-theme scale-tailwind"
      mainClassName="py-8 px-[160px] overflow-y-auto"
      header={<CorpProjectGroupManagerHomeHeader />}
      headerClassName="border-b shadow-sm border-b-neutral-200 flex justify-between w-full items-center py-2 px-4 bg-white gap-4"
    >
      <div className="w-full space-y-12">
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="2"
            onClick={() => {
              setShowModal(true);
            }}
          >
            Project Role Checkup
          </Button>
        </div>
        {filteredSections.map(section => (
          <Section
            key={section.title}
            title={section.title}
            items={section.items.filter(item => {
              if (item.key === 'trust-and-safety-center' && !canSeeTnsCenter) {
                return false;
              }
              if (item.key === 'gaius-projects-center' && !gaiusProjectsCenterEnabled) {
                return false;
              }
              if (item.key === 'webinar-hub' && !webinarHubFlag) {
                return false;
              }
              return true;
            })}
          />
        ))}
      </div>
    </AppLayout>
  );
}
